import { ApolloError, useMutation } from '@apollo/client';
import classNames from 'classnames';
import { Button } from 'components/Button';
import { withNotification } from 'components/Notification';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { Toggle } from 'components/Toggle';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import {
  UPDATE_RETURN_SHIPPING_OPTIONS_SETTINGS,
  UPDATE_STORE,
} from 'gql/mutations';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import { useDropzone } from 'react-dropzone';
import { CloudUploadIcon, XIcon } from '@heroicons/react/outline';
import { useSettingsSaveState } from 'hooks/useSettingsSaveState';
import {
  EMAIL_MAX_LENGTH,
  PHONE_MAX_LENGTH,
  isValidEmailInput,
  isValidHttpUrlInput,
  isValidPhoneInput,
  sanitizePhoneInput,
} from 'utils/settingsInput';
import ReturnPolicyLinksSettings, {
  getReturnPolicyLinksError,
  getReturnPolicyLinksForm,
  getReturnPolicyLinksPayload,
} from './ReturnPolicyLinksSettings';

const GENERAL_FIELD_LIMITS = {
  companyName: 100,
  url: 2048,
} as const;

type TeamDataErrors = Partial<
  Record<
    | 'companyName'
    | 'companyEmail'
    | 'companyPhone'
    | 'policyUrl'
    | 'returnPolicyLinks',
    string
  >
>;

const EMPTY_TEAM_DATA = {
  companyName: '',
  companyEmail: '',
  companyPhone: '',
  companyWebsite: '',
  logoUrl: '',
  requireDeliveryGuaranteePolicy: false,
  policyUrl: '',
  website: '',
};

const General = withNotification(
  ({ showNotification }: { showNotification: any }) => {
    const { storeId, storeProperties, refetchStoreProperties } = useStore();

    const [teamData, setTeamData] = useState(EMPTY_TEAM_DATA);
    const [teamDataErrors, setTeamDataErrors] = useState<TeamDataErrors>({});
    const [returnPolicyLinks, setReturnPolicyLinks] = useState(() =>
      getReturnPolicyLinksForm(),
    );
    const hydratedStoreIdRef = useRef<string | null>(null);

    useEffect(() => {
      if (storeId && hydratedStoreIdRef.current === storeId) {
        if (storeProperties?.id === storeId) {
          setTeamData((current) => ({
            ...current,
            logoUrl: storeProperties.logoUrl ?? '',
          }));
        }
        return;
      }
      hydratedStoreIdRef.current = null;
      setTeamData(EMPTY_TEAM_DATA);
      setReturnPolicyLinks(getReturnPolicyLinksForm());
      setTeamDataErrors({});
      if (storeId && storeProperties?.id === storeId) {
        setTeamData({
          companyName: storeProperties?.name ?? '',
          companyEmail: storeProperties?.contactEmail ?? '',
          companyPhone: storeProperties?.contactPhone ?? '',
          companyWebsite: storeProperties?.store ?? '',
          logoUrl: storeProperties?.logoUrl ?? '',
          requireDeliveryGuaranteePolicy:
            storeProperties?.requireDeliveryGuaranteePolicy ?? false,
          policyUrl: storeProperties?.policyUrl ?? '',
          website: storeProperties?.url ?? '',
        });
        setReturnPolicyLinks(
          getReturnPolicyLinksForm(
            storeProperties.policySettings?.returnPolicyLinks,
          ),
        );
        hydratedStoreIdRef.current = storeId;
      }
    }, [storeId, storeProperties]);
    const generalFormState = useMemo(
      () => ({
        companyName: teamData.companyName,
        companyEmail: teamData.companyEmail,
        companyPhone: teamData.companyPhone,
        requireDeliveryGuaranteePolicy: teamData.requireDeliveryGuaranteePolicy,
        policyUrl: teamData.policyUrl,
        website: teamData.website,
        returnPolicyLinks,
      }),
      [teamData, returnPolicyLinks],
    );
    const generalResetKey = useMemo(
      () =>
        JSON.stringify({
          storeId,
          ready: Boolean(storeId && storeProperties?.id === storeId),
        }),
      [storeId, storeProperties],
    );
    const { hasUnsavedChanges, markSaved, markSaveFailed } =
      useSettingsSaveState(generalFormState, generalResetKey);
    const [
      updateStore,
      { error: updateStoreError, loading: updateStoreLoading },
    ] = useMutation(UPDATE_STORE, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

    const [updateReturnSettings, { loading: updateReturnSettingsLoading }] =
      useMutation(UPDATE_RETURN_SHIPPING_OPTIONS_SETTINGS);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    const handleCompanyLogoUpdate = useCallback(
      (imgUrl: string, field: string, label: string = 'Image') => {
        updateStore({
          variables: { storeId, set: { [field]: imgUrl } },
        })
          .then((data) => {
            refetchStoreProperties && refetchStoreProperties();
            return showNotification(
              NotificationTypes.success,
              `${label} updated`,
            );
          })
          .catch((err) => {
            return showNotification(
              NotificationTypes.error,
              updateStoreError
                ? updateStoreError.message
                : `Could not update ${label.toLowerCase()} photo`,
            );
          });
      },
      [
        storeId,
        updateStore,
        refetchStoreProperties,
        showNotification,
        updateStoreError,
      ],
    );

    const uploadLogo = useCallback(
      async (files: File[]) => {
        const uploadURL = `${process.env.REACT_APP_API_BASE_URL}/media/upload`;

        try {
          setIsUploadingLogo(true);
          const file = files[0];
          const fd = new FormData();
          fd.append('file', file);

          const response = await fetch(uploadURL, {
            method: 'POST',
            body: fd,
          });

          const result = await response.json();
          const { url } = result;
          handleCompanyLogoUpdate(url, 'logoUrl', 'Company logo');
        } catch (error) {
          showNotification(
            NotificationTypes.error,
            'Failed to upload logo. Please try again.',
          );
        } finally {
          setIsUploadingLogo(false);
        }
      },
      [handleCompanyLogoUpdate, showNotification],
    );

    const onDropLogo = useCallback(
      (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
          uploadLogo(acceptedFiles);
        }
      },
      [uploadLogo],
    );

    const {
      getRootProps: getLogoRootProps,
      getInputProps: getLogoInputProps,
      isDragActive: isLogoDragActive,
    } = useDropzone({
      onDrop: onDropLogo,
      accept: 'image/*',
      multiple: false,
      maxSize: 10485760, // 10MB
    });

    const handleChangeTeam = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = event.target;
      const nextValue =
        name === 'companyPhone' ? sanitizePhoneInput(value) : value;
      setTeamData((current) => ({ ...current, [name]: nextValue }));
      setTeamDataErrors((current) => {
        if (!(name in current)) return current;
        const next = { ...current };
        delete next[name as keyof TeamDataErrors];
        return next;
      });
    };

    const handleUpdateStore = async (): Promise<boolean> => {
      if (!storeId || storeProperties?.id !== storeId) {
        showNotification(
          NotificationTypes.error,
          'The selected store is still loading. Please try again in a moment.',
        );
        return false;
      }
      const errors: TeamDataErrors = {};
      if (teamData.companyName.length > GENERAL_FIELD_LIMITS.companyName) {
        errors.companyName = `Company name must be ${GENERAL_FIELD_LIMITS.companyName} characters or fewer.`;
      }
      const policyLinksError = getReturnPolicyLinksError(returnPolicyLinks);
      if (policyLinksError) errors.returnPolicyLinks = policyLinksError;
      if (!isValidEmailInput(teamData.companyEmail)) {
        errors.companyEmail = 'Enter a valid support contact email address.';
      }
      if (!isValidPhoneInput(teamData.companyPhone)) {
        errors.companyPhone =
          'Enter a valid phone number using numbers and + ( ) . -.';
      }
      if (
        teamData.requireDeliveryGuaranteePolicy &&
        !teamData.policyUrl.trim()
      ) {
        errors.policyUrl = 'Enter a policy URL.';
      } else if (!isValidHttpUrlInput(teamData.policyUrl)) {
        errors.policyUrl =
          'Enter a valid return policy URL beginning with http:// or https://.';
      }

      if (Object.keys(errors).length > 0) {
        setTeamDataErrors(errors);
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          Object.values(errors)[0] || 'Review the highlighted fields.',
        );
        return false;
      }

      let policyLinksSaved = false;
      try {
        await updateReturnSettings({
          variables: {
            input: {
              storeId,
              policySettingsPatch: {
                returnPolicyLinks:
                  getReturnPolicyLinksPayload(returnPolicyLinks),
              },
            },
          },
        });
        policyLinksSaved = true;
        await updateStore({
          variables: {
            storeId,
            set: {
              contactEmail: teamData.companyEmail.trim(),
              name: teamData.companyName.trim(),
              contactPhone: teamData.companyPhone.trim(),
              url: teamData.website.trim(),
              policyUrl: teamData.policyUrl.trim(),
              requireDeliveryGuaranteePolicy:
                teamData.requireDeliveryGuaranteePolicy,
            },
          },
        });
        markSaved();
        setTeamDataErrors({});
        showNotification(NotificationTypes.success, 'Company details updated');
        await refetchStoreProperties?.();
        return true;
      } catch (error) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          policyLinksSaved
            ? 'Return policy links saved, but company details could not be saved. Retry Save to finish.'
            : (error instanceof ApolloError
                ? apiErrorHandler(error)?.message
                : undefined) ||
                'Return policy links could not be saved. Company details were not changed.',
        );
        return false;
      }
    };
    return (
      <SettingsViewWrapper
        sectionId="resolution-portal"
        activeNav="resolution-portal/general"
      >
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleUpdateStore}
        />
        <div className="w-full h-full bg-[#f6f6f7]">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
            {/* Header */}
            <header className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-[28px] font-semibold text-[#202223] leading-tight mb-2">
                  General Settings
                </h1>
                <p className="text-[#6d7175] text-sm max-w-2xl">
                  Manage core details for how your brand is displayed inside the
                  Resolution Portal.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Button
                  onClick={handleUpdateStore}
                  size={SizeEnum.md}
                  className="px-4 py-2 bg-[#2563EB] text-white text-sm font-medium rounded-md hover:bg-[#1d4ed8] transition-colors"
                  isLoading={updateStoreLoading || updateReturnSettingsLoading}
                >
                  Save
                </Button>
              </div>
            </header>

            {/* Card: Company Logo */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6 space-y-6">
              <div>
                <h2 className="text-base font-semibold text-[#202223] mb-1">
                  Company Logo
                </h2>
                <p className="text-[#6d7175] text-sm">
                  Upload your company logo. Recommended: square format, minimum
                  150 × 150px. This logo will be displayed in the Resolution
                  Portal.
                </p>
              </div>

              <div className="flex flex-col md:flex-row items-start gap-6">
                {/* Preview */}
                <div className="w-32 h-32 bg-[#f6f6f7] border border-[#e1e3e5] rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {teamData?.logoUrl ? (
                    <img
                      src={teamData.logoUrl}
                      alt="Company logo preview"
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <p className="text-[#6d7175] text-xs font-medium mb-1">
                        Logo Preview
                      </p>
                      <p className="text-[#8c9196] text-xs">No logo uploaded</p>
                    </div>
                  )}
                </div>

                {/* Upload Area */}
                <div className="flex-1 w-full">
                  <div
                    {...getLogoRootProps()}
                    className={classNames(
                      'relative border-2 border-dashed rounded-lg p-8 transition-all cursor-pointer',
                      isLogoDragActive
                        ? 'border-[#2563EB] bg-[#eff6ff]'
                        : 'border-[#c9cccf] bg-[#fafbfb] hover:border-[#2563EB] hover:bg-[#f6f6f7]',
                    )}
                  >
                    <input {...getLogoInputProps()} />
                    <div className="flex flex-col items-center justify-center text-center">
                      {isUploadingLogo ? (
                        <>
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2563EB] mb-4"></div>
                          <p className="text-sm font-medium text-[#202223]">
                            Uploading company logo...
                          </p>
                          <p className="text-xs text-[#6d7175] mt-1">
                            Please wait
                          </p>
                        </>
                      ) : (
                        <>
                          <CloudUploadIcon
                            className={classNames(
                              'w-12 h-12 mb-4 transition-colors',
                              isLogoDragActive
                                ? 'text-[#2563EB]'
                                : 'text-[#6d7175]',
                            )}
                          />
                          <p className="text-sm font-medium text-[#202223] mb-1">
                            {isLogoDragActive
                              ? 'Drop your company logo here'
                              : 'Upload company logo'}
                          </p>
                          <p className="text-xs text-[#6d7175] mb-2">
                            Drag and drop your logo, or click to browse
                          </p>
                          <p className="text-xs text-[#8c9196]">
                            PNG, JPG, GIF up to 10MB
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {teamData?.logoUrl && !isUploadingLogo && (
                    <div className="mt-4 flex items-center gap-4">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompanyLogoUpdate(
                            '',
                            'logoUrl',
                            'Company logo',
                          );
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#dc2626] hover:text-[#b91c1c] hover:bg-[#fef2f2] rounded-md transition-colors"
                      >
                        <XIcon className="w-4 h-4" />
                        Remove logo
                      </button>
                      <p className="text-xs text-[#6d7175]">
                        Your logo is synced from Shopify and will update
                        automatically
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Card: Company Info */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6 space-y-6">
              <h2 className="text-base font-semibold text-[#202223]">
                Company Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Support contact email */}
                <div className="space-y-2">
                  <label
                    htmlFor="companyEmail"
                    className="block text-sm font-medium text-[#202223]"
                  >
                    Support contact email
                  </label>
                  <p
                    id="support-contact-email-help"
                    className="text-xs text-[#6d7175]"
                  >
                    Used by Contact Support on denied return pages. If no email
                    is set, that option is hidden.
                  </p>
                  <input
                    type="email"
                    name="companyEmail"
                    id="companyEmail"
                    className="w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#202223] focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] transition-all bg-white hover:border-[#a6acb3]"
                    placeholder="you@example.com"
                    value={teamData.companyEmail}
                    onChange={handleChangeTeam}
                    maxLength={EMAIL_MAX_LENGTH}
                    aria-invalid={Boolean(teamDataErrors.companyEmail)}
                    aria-describedby="support-contact-email-help"
                  />
                  {teamDataErrors.companyEmail && (
                    <p role="alert" className="text-xs text-red-600">
                      {teamDataErrors.companyEmail}
                    </p>
                  )}
                </div>

                {/* Company Name */}
                <div className="space-y-2">
                  <label
                    htmlFor="companyName"
                    className="block text-sm font-medium text-[#202223]"
                  >
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    id="companyName"
                    className="w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#202223] focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] transition-all bg-white hover:border-[#a6acb3]"
                    placeholder="Your Company Name"
                    value={teamData.companyName}
                    onChange={handleChangeTeam}
                    maxLength={GENERAL_FIELD_LIMITS.companyName}
                    aria-invalid={Boolean(teamDataErrors.companyName)}
                  />
                  {teamDataErrors.companyName && (
                    <p role="alert" className="text-xs text-red-600">
                      {teamDataErrors.companyName}
                    </p>
                  )}
                </div>

                {/* Company Phone */}
                <div className="space-y-2">
                  <label
                    htmlFor="companyPhone"
                    className="block text-sm font-medium text-[#202223]"
                  >
                    Company Phone
                  </label>
                  <input
                    type="tel"
                    inputMode="tel"
                    name="companyPhone"
                    id="companyPhone"
                    className="w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#202223] focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] transition-all bg-white hover:border-[#a6acb3]"
                    placeholder="eg: +1-541-754-3010"
                    value={teamData.companyPhone}
                    onChange={handleChangeTeam}
                    maxLength={PHONE_MAX_LENGTH}
                    aria-invalid={Boolean(teamDataErrors.companyPhone)}
                  />
                  {teamDataErrors.companyPhone && (
                    <p role="alert" className="text-xs text-red-600">
                      {teamDataErrors.companyPhone}
                    </p>
                  )}
                </div>

                {/* Company Store Domain */}
                <div className="space-y-2">
                  <label
                    htmlFor="storeDomain"
                    className="block text-sm font-medium text-[#202223]"
                  >
                    Store Domain
                  </label>
                  <input
                    type="text"
                    id="storeDomain"
                    disabled
                    className="w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#6d7175] bg-[#f6f6f7] cursor-not-allowed"
                    value={teamData.companyWebsite || 'N/A'}
                  />
                </div>
                {/* Company Website URL */}
                <div className="space-y-2">
                  <label
                    htmlFor="website"
                    className="block text-sm font-medium text-[#202223]"
                  >
                    Website
                  </label>
                  <input
                    type="text"
                    id="website"
                    className="w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#6d7175]"
                    value={teamData.website || 'N/A'}
                  />
                </div>
              </div>
            </section>

            {/* Card: Refund Policy */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6 space-y-6">
              <h2 className="text-base font-semibold text-[#202223]">
                Refund Policy
              </h2>

              <div className="space-y-4 max-w-xl">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-[#202223]">
                    Require Refund Policy
                  </label>
                  <Toggle
                    isEnabled={teamData.requireDeliveryGuaranteePolicy}
                    onChange={(v: boolean) =>
                      setTeamData({
                        ...teamData,
                        requireDeliveryGuaranteePolicy: v,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="policyUrl"
                    className="block text-sm font-medium text-[#202223]"
                  >
                    Return policy URL
                  </label>
                  <p
                    id="return-policy-url-help"
                    className="text-xs text-[#6d7175]"
                  >
                    Used by View Return Policy on denied return pages. If no URL
                    is set, that option is hidden.
                  </p>
                  <input
                    type="url"
                    name="policyUrl"
                    id="policyUrl"
                    className="w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#202223] focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] transition-all bg-white hover:border-[#a6acb3]"
                    placeholder="https://"
                    value={teamData.policyUrl}
                    onChange={handleChangeTeam}
                    maxLength={GENERAL_FIELD_LIMITS.url}
                    aria-invalid={Boolean(teamDataErrors.policyUrl)}
                    aria-describedby="return-policy-url-help"
                  />
                  {teamDataErrors.policyUrl && (
                    <p role="alert" className="text-xs text-red-600">
                      {teamDataErrors.policyUrl}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <ReturnPolicyLinksSettings
              value={returnPolicyLinks}
              onChange={(value) => {
                setReturnPolicyLinks(value);
                setTeamDataErrors((current) => ({
                  ...current,
                  returnPolicyLinks: undefined,
                }));
              }}
              error={teamDataErrors.returnPolicyLinks}
            />

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-[#e1e3e5]">
              <Button
                onClick={handleUpdateStore}
                size={SizeEnum.md}
                className="px-4 py-2 bg-[#2563EB] text-white text-sm font-medium rounded-md hover:bg-[#1d4ed8] transition-colors"
                isLoading={updateStoreLoading || updateReturnSettingsLoading}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </SettingsViewWrapper>
    );
  },
);

export default General;
